import {gql} from '@apollo/client';
import {compact, groupByUniqueKey} from '@benchling/js-utils';
import {useMemo} from 'react';

import {SuccessReturnType} from 'graphql-client/BenchlingQuery';
import {QueryResultType, useBenchlingQuery, BenchlingQueryResult} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {SchemaById} from 'lookup-config/LookupConfigModalConstants';
import {mapSchemaInterfaceGraphQLToSchemaInterfaceExtendingSchema} from 'lookup-config/LookupConfigUtils';
import {ProcessesAllSchemasQuery} from 'processes/modals/utils/__generated/ProcessesAllSchemasQuery';
import {Schema} from 'schemas/SchemaConstants';
import {omit} from 'util/omitKey';

export const PROCESSES_ALL_SCHEMAS_QUERY = gql`
  query ProcessesAllSchemasQuery @skipAudit @engComponent(name: WORKFLOWS) {
    tagSchemas {
      ...TagSchemaGraphQL
      registry {
        ...RegistryGraphQL
      }
    }
    boxSchemas {
      ...BoxSchemaGraphQL
      registry {
        ...RegistryGraphQL
      }
    }
    containerSchemas {
      ...ContainerSchemaGraphQL
      registry {
        ...RegistryGraphQL
      }
    }
    locationSchemas {
      ...LocationSchemaGraphQL
      registry {
        ...RegistryGraphQL
      }
    }
    plateSchemas {
      ...PlateSchemaGraphQL
      registry {
        ...RegistryGraphQL
      }
    }
    ...AllResultSchemasGraphQL
    ...AllProcessTaskSchemasForSchemaConfigGraphQL
    ...AllSchemaInterfacesGraphQL
  }

  ${getFragmentWrapper('TagSchema').fragment}
  ${getFragmentWrapper('AllSchemaInterfaces').fragment}
  ${getFragmentWrapper('AllResultSchemas').fragment}
  ${getFragmentWrapper('AllProcessTaskSchemasForSchemaConfig').fragment}
  ${getFragmentWrapper('BoxSchema').fragment}
  ${getFragmentWrapper('ContainerSchema').fragment}
  ${getFragmentWrapper('LocationSchema').fragment}
  ${getFragmentWrapper('PlateSchema').fragment}
  ${getFragmentWrapper('Registry').fragment}
`;

export const PROCESSES_TAG_SCHEMAS_INTERFACES_QUERY = gql`
  query ProcessesTagSchemasInterfacesQuery @skipAudit @engComponent(name: WORKFLOWS) {
    tagSchemas {
      id
      interfaces {
        id
      }
    }
  }
`;

interface SchemaWithFieldsJson {
  id: string;
  name: string;
  fieldsJson: JSONScalar;
  allFieldsJson: JSONScalar;
}

function doesSchemaHaveFieldsJson(schema: SchemaWithFieldsJson | Schema): schema is SchemaWithFieldsJson {
  return (schema as SchemaWithFieldsJson).fieldsJson != null;
}

export function getAllSchemasByIdFromQuery(
  allSchemasQueryResult: SuccessReturnType<ProcessesAllSchemasQuery>
): SchemaById {
  return groupByUniqueKey(
    [
      ...Object.values(omit(allSchemasQueryResult.data, '__typename', 'schemaInterfaces'))
        .map((schemaList) => schemaList)
        .flat(),
      ...compact(
        allSchemasQueryResult.data.processTaskSchemasSupportingPlatformFeature.map(
          ({outputSchema}) => outputSchema
        )
      ),
    ]
      .map(
        (schema: SchemaWithFieldsJson | Schema): Schema =>
          doesSchemaHaveFieldsJson(schema)
            ? {...schema, fields: schema.fieldsJson, allFields: schema.allFieldsJson}
            : schema
      )
      .concat(
        allSchemasQueryResult.data.schemaInterfaces.map((schemaInterface) =>
          mapSchemaInterfaceGraphQLToSchemaInterfaceExtendingSchema(schemaInterface)
        )
      ),
    ({id}) => id
  );
}

export function useAllSchemasById(): {
  allSchemasById: SchemaById;
  allSchemasQueryResult: BenchlingQueryResult<ProcessesAllSchemasQuery>;
} {
  const allSchemasQueryResult = useBenchlingQuery<ProcessesAllSchemasQuery>(PROCESSES_ALL_SCHEMAS_QUERY);
  const allSchemasById = useMemo(
    () =>
      allSchemasQueryResult.type === QueryResultType.SUCCESS
        ? getAllSchemasByIdFromQuery(allSchemasQueryResult)
        : {},
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [allSchemasQueryResult.type]
  );
  return {allSchemasById, allSchemasQueryResult};
}
